<script>
    import { toast } from "@zerodevx/svelte-toast";
    import {
        Button,
        Checkbox,
        Input,
        Label,
        Modal,
        P,
        Spinner,
        Table,
        TableBody,
        TableBodyCell,
        TableBodyRow,
        TableHead,
        TableHeadCell,
    } from "flowbite-svelte";
    import {
        ClipboardOutline,
        PlusOutline,
        TrashBinOutline,
        TerminalOutline,
    } from "flowbite-svelte-icons";
    import { onMount } from "svelte";
    import { make_request } from "../../service/http_calls";
    import { global_state } from "../../service/store";
    import { writable } from "svelte/store";
    import { getNestedDict, updateNestedDict } from "../../service/sse";

    const component_address = ["profile", "users"];
    let component_state = writable({});
    let loading = false;

    $: loading = $component_state?.data?.completed === "false";

    let error = null;

    let showModal = false;
    let showCredentialsModal = false;
    let selectedUser = null;
    let newUser = {
        email: "",
        name: "",
    };

    onMount(async () => {
        await getUsers();
    });

    global_state.subscribe((state) => {
        const current_state = getNestedDict(state, component_address);
        component_state.set(current_state);
        console.log("current_state", current_state?.data?.users);
        if (current_state?.new_user?.data?.completed === "true") {
            console.log("User created, fetching users");
            updateNestedDict(
                state,
                [...component_address, "new_user", "data", "completed"], "false",
            );
            getUsers();
        }

        if (current_state?.delete_user?.data?.completed === "true") {
            console.log("User deleted, fetching users");
            updateNestedDict(
                state,
                [...component_address, "delete_user", "data", "completed"], "false",
            );
            getUsers();
        }

        if (current_state?.credentials?.data?.completed === "true") {
            console.log("Credentials fetched, showing modal", current_state?.credentials?.data);
            showCredentialsModal = true;
            if (selectedUser) {
                selectedUser.client_id = current_state?.credentials?.data?.user?.client_id;
                selectedUser.client_secret =
                    current_state?.credentials?.data?.user?.client_secret;
            }
            updateNestedDict(
                state,
                [...component_address, "credentials", "data", "completed"], "false",
            );
        }
    });

    async function addUser() {
        const request_id = crypto.randomUUID();
        await make_request(
            {
                type: "users",
                sub_type: "create_user_by_user",
                request_id: request_id,
                payload: {
                    email: newUser.email,
                    name: newUser.name,
                },
            },
            component_address,
            {
                completed: [
                    ...component_address,
                    "new_user",
                    "data",
                    "completed",
                ],
                users: [...component_address, "new_user", "data", "users"],
            },
        );
        showModal = false;
    }

    async function getNewCredentials(user) {
        console.log("Getting new credentials for user", user);
        const request_id = crypto.randomUUID();
        await make_request(
            {
                type: "users",
                sub_type: "get_new_credentials",
                request_id: request_id,
                payload: {
                    user_id: user.id,
                },
            },
            component_address,
            {
                completed: [
                    ...component_address,
                    "credentials",
                    "data",
                    "completed",
                ],
                data: [...component_address, "credentials", "data", "user"],
            },
        );
    }
    async function getUsers() {
        console.log("Getting users");
        component_state.update((state) => {
            if (state) {
                if (state.data) {
                    state.data.users = [];
                    state.data.completed = "false";
                }
            }
            return state;
        });
        const request_id = crypto.randomUUID();
        await make_request(
            {
                type: "users",
                sub_type: "list_users_by_user",
                request_id: request_id,
                payload: {},
            },
            component_address,
            {
                completed: [...component_address, "data", "completed"],
                data: [...component_address, "data", "users"],
            },
        );
    }

    function copyToClipboard(text) {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                toast.push("Copied to clipboard");
            })
            .catch((err) => {
                toast.push("Failed to copy", { classes: ["error"] });
                console.error("Failed to copy: ", err);
            });
    }

    async function deleteUser(userId) {
        console.log("Deleting user", userId);
        const request_id = crypto.randomUUID();
        await make_request(
            {
                type: "users",
                sub_type: "delete_user_by_user",
                request_id: request_id,
                payload: {
                    id: userId,
                },
            },
            component_address,
            {
                completed: [
                    ...component_address,
                    "delete_user",
                    "data",
                    "completed",
                ],
            },
        );
    }

    async function showUserCredentials(user) {
        selectedUser = user;
        console.log("Showing user credentials", user);
        await getNewCredentials(user);
        // showCredentialsModal = true;
    }
</script>

<div class="container mx-auto px-4 py-8">
    <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold">User Administration</h1>
        <Button
            color="blue"
            class="flex items-center gap-2"
            on:click={() => (showModal = true)}
        >
            <PlusOutline class="w-4 h-4" />
            Add User
        </Button>
    </div>

    {#if loading}
        <div class="flex flex-col justify-center items-center h-40">
            <Spinner size="lg" color="blue" />
            <P class="text-center py-4 text-gray-600">Loading users...</P>
        </div>
    {:else if error}
        <P class="text-center py-8 text-red-500">{error}</P>
    {:else if $component_state?.data?.users?.length === 0}
        <P class="text-center py-8"
            >No users found. Add a new user to get started.</P
        >
    {:else}
        <div class="overflow-x-auto shadow-md rounded-lg">
            <Table striped={true}>
                <TableHead>
                    <TableHeadCell>ID</TableHeadCell>
                    <TableHeadCell>Name</TableHeadCell>
                    <TableHeadCell>Email</TableHeadCell>
                    <TableHeadCell>Credentials</TableHeadCell>
                    <TableHeadCell>Actions</TableHeadCell>
                </TableHead>
                <TableBody>
                    {#each Object.values($component_state?.data?.users ?? []) as user}
                        <TableBodyRow>
                            <TableBodyCell>{user.id}</TableBodyCell>
                            <TableBodyCell>{user.full_name || '-'}</TableBodyCell>
                            <TableBodyCell>{user.email}</TableBodyCell>
                            <TableBodyCell>
                                <button
                                    class="text-gray-500 hover:text-blue-600 flex items-center gap-1"
                                    on:click={() => showUserCredentials(user)}
                                    title="View Credentials"
                                >
                                    <TerminalOutline class="w-4 h-4" />
                                    <span>View Credentials</span>
                                </button>
                            </TableBodyCell>
                            <TableBodyCell>
                                <button
                                    class="text-gray-500 hover:text-red-600"
                                    on:click={() => deleteUser(user.id)}
                                    title="Delete User"
                                >
                                    <TrashBinOutline class="w-4 h-4" />
                                </button>
                            </TableBodyCell>
                        </TableBodyRow>
                    {/each}
                </TableBody>
            </Table>
        </div>
    {/if}
</div>

<!-- Add User Modal -->
<Modal bind:open={showModal} size="md" autoclose={false} title="Add New User">
    <form on:submit|preventDefault={addUser} class="space-y-4">
        <div>
            <Label for="email" class="mb-2">Email</Label>
            <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                required
                bind:value={newUser.email}
            />
        </div>
        <div>
            <Label for="name" class="mb-2">Name</Label>
            <Input
                id="name"
                type="text"
                placeholder="User Name"
                required
                bind:value={newUser.name}
            />
        </div>
        <div class="flex justify-end gap-2">
            <Button color="alternative" on:click={() => (showModal = false)}
                >Cancel</Button
            >
            <Button type="submit" color="blue">Add User</Button>
        </div>
    </form>
</Modal>

<!-- User Credentials Modal -->
<Modal bind:open={showCredentialsModal} size="md" autoclose={false} title="User Credentials">
    {#if selectedUser}
        <div class="space-y-4">
            <div>
                <Label class="mb-2">Client ID</Label>
                <div class="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                    <span class="font-mono text-sm truncate flex-1">{selectedUser.client_id}</span>
                    <button
                        class="text-gray-500 hover:text-blue-600"
                        on:click={() => copyToClipboard(selectedUser.client_id)}
                        title="Copy Client ID"
                    >
                        <ClipboardOutline class="w-4 h-4" />
                    </button>
                </div>
            </div>
            <div>
                <Label class="mb-2">Client Secret</Label>
                <div class="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                    <span class="font-mono text-sm truncate flex-1">{selectedUser.client_secret}</span>
                    <button
                        class="text-gray-500 hover:text-blue-600"
                        on:click={() => copyToClipboard(selectedUser.client_secret)}
                        title="Copy Secret"
                    >
                        <ClipboardOutline class="w-4 h-4" />
                    </button>
                </div>
            </div>
            <div class="flex justify-end">
                <Button color="alternative" on:click={() => (showCredentialsModal = false)}>Close</Button>
            </div>
        </div>
    {/if}
</Modal>
